import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { motion } from "framer-motion";
import { IoBagOutline, IoSearchOutline, IoCallOutline, IoLogOutOutline, IoCartOutline } from "react-icons/io5";
import { BtnContent } from "./components/BtnIcon";
import "./styles/App.css";
import "./styles/professional-shell.css";
import "./styles/luxury-home.css";
import Loading from "./components/Loading";
import CartDrawer from "./components/luxury/CartDrawer";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import CheckoutVerify from "./pages/CheckoutVerify";
import About from "./pages/About";
import ComingSoon from "./pages/ComingSoon";
import { EmailIcon, LocationIcon } from "./components/Icons";
import ProductDetails from "./pages/Product";
import GiftCustomise from "./pages/GiftCustomise";
import { normalizeGiftLidLayout, normalizeGiftLidTextColor } from "./components/GiftBoxLidEditor";
import Login from "./pages/Login";
import OrderHistory from "./pages/OrderHistory";
import Admin from "./pages/Admin";
import SareeGuide from "./pages/SareeGuide";
import Catalogue from "./pages/Catalogue";
import Privacy from "./pages/Privacy";
import Shipping from "./pages/Shipping";
import Returns from "./pages/Returns";
import WhatsAppFloat from "./components/WhatsAppFloat";
import ScrollToTop from "./components/ScrollToTop";
import { clearCustomerSession, getCustomerSession } from "./utils/customerSession";
import { CONTACT_EMAIL, INSTAGRAM_URL, BUSINESS_NAME, BUSINESS_CARE_OF, BUSINESS_ADDRESS_LINES, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_E164, APP_LOGO, APP_LOGO_ALT, UDYAM_REGISTRATION_NUMBER } from "./config/site";
import { applyPageSeo } from "./config/pageSeo";

function App() {
  // Set to true to show coming soon page, false for full app
  const [isComingSoon] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedCart = localStorage.getItem("cart");
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCart(parsedCart);
        updateCartCount(parsedCart);
      } catch (error) {
        console.error("Error parsing cart from localStorage:", error);
      }
    }
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  const updateCartCount = (cartItems) => {
    const count = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(count);
  };

  const addToCart = (product, options = {}) => {
    if (!product) return;
    const hasMsg = Object.prototype.hasOwnProperty.call(options, "giftLidMessage");
    const hasLayout = Object.prototype.hasOwnProperty.call(options, "giftLidLayout");
    const hasTextColor = Object.prototype.hasOwnProperty.call(options, "giftLidTextColor");
    const giftLidMessage = hasMsg
      ? String(options.giftLidMessage ?? "").trim().slice(0, 200) || undefined
      : undefined;
    const giftLidLayout = hasLayout ? normalizeGiftLidLayout(options.giftLidLayout) : undefined;
    const giftLidTextColor = hasTextColor
      ? normalizeGiftLidTextColor(options.giftLidTextColor)
      : undefined;

    const addQty = Math.max(1, Number(options.quantity) || 1);

    const existing = cart.find((item) => item.id === product.id);
    let newCart;
    if (existing) {
      newCart = cart.map((item) =>
        item.id === product.id
          ? {
              ...item,
              quantity: item.quantity + addQty,
              ...(hasMsg ? { giftLidMessage } : {}),
              ...(hasLayout ? { giftLidLayout } : {}),
              ...(hasTextColor ? { giftLidTextColor } : {}),
            }
          : item,
      );
    } else {
      newCart = [
        ...cart,
        {
          ...product,
          quantity: addQty,
          ...(hasMsg ? { giftLidMessage } : {}),
          ...(hasLayout ? { giftLidLayout } : {}),
          ...(hasTextColor ? { giftLidTextColor } : {}),
        },
      ];
    }
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    updateCartCount(newCart);
  };

  const removeFromCart = (id) => {
    const newCart = cart.filter((item) => item.id !== id);
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    updateCartCount(newCart);
  };

  const updateQuantity = (id, quantity) => {
    if (quantity < 1) {
      removeFromCart(id);
      return;
    }
    const newCart = cart.map((item) =>
      item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item,
    );
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    updateCartCount(newCart);
  };

  const updateCartItem = (id, patch) => {
    const newCart = cart.map((item) =>
      String(item.id) === String(id) ? { ...item, ...patch } : item,
    );
    setCart(newCart);
    localStorage.setItem("cart", JSON.stringify(newCart));
    updateCartCount(newCart);
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("cart");
    setCartCount(0);
  };

  // Show Coming Soon page if flag is set
  if (isComingSoon) {
    return <ComingSoon />;
  }

  return (
    <Router>
      <ScrollToTop />
      <AppRoutesShell
        isLoading={isLoading}
        handleLoadingComplete={handleLoadingComplete}
        cart={cart}
        cartCount={cartCount}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        updateQuantity={updateQuantity}
        updateCartItem={updateCartItem}
        clearCart={clearCart}
      />
    </Router>
  );
}

function AppRoutesShell({
  isLoading,
  handleLoadingComplete,
  cart,
  cartCount,
  addToCart,
  removeFromCart,
  updateQuantity,
  updateCartItem,
  clearCart,
}) {
  const location = useLocation();
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  useEffect(() => {
    applyPageSeo(location.pathname);
  }, [location.pathname]);

  return (
    <div className="app">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      {isLoading && <Loading onComplete={handleLoadingComplete} />}
      <Navigation
        cartCount={cartCount}
        onOpenCart={() => setCartDrawerOpen(true)}
      />
      <CartDrawer
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        cart={cart}
        removeFromCart={removeFromCart}
        updateQuantity={updateQuantity}
      />
      <FloatingCart
        cartCount={cartCount}
        onOpenCart={() => setCartDrawerOpen(true)}
      />
      <WhatsAppFloat />
      <motion.main
        id="main-content"
        className="site-main"
        tabIndex={-1}
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <Routes>
          <Route path="/" element={<Home addToCart={addToCart} />} />
          <Route path="/shop" element={<Shop addToCart={addToCart} />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/gift-guide" element={<SareeGuide />} />
          <Route path="/saree-guide" element={<SareeGuide />} />
          <Route
            path="/cart"
            element={
              <Cart
                cart={cart}
                removeFromCart={removeFromCart}
                updateQuantity={updateQuantity}
              />
            }
          />
          <Route
            path="/product/:id"
            element={<ProductDetails addToCart={addToCart} />}
          />
          <Route
            path="/customise/:productId"
            element={<GiftCustomise addToCart={addToCart} updateCartItem={updateCartItem} />}
          />
          <Route
            path="/customise"
            element={<GiftCustomise addToCart={addToCart} updateCartItem={updateCartItem} />}
          />
          <Route
            path="/checkout-verify"
            element={<CheckoutVerify cart={cart} />}
          />
          <Route
            path="/checkout"
            element={<Checkout cart={cart} clearCart={clearCart} />}
          />
          <Route path="/login" element={<Login />} />
          <Route path="/orders" element={<OrderHistory />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </motion.main>
      <Footer />
    </div>
  );
}
function FloatingCart({ cartCount, onOpenCart }) {
  const navigate = useNavigate();
  const location = useLocation();

  if (
    cartCount === 0 ||
    location.pathname === "/cart" ||
    location.pathname === "/checkout" ||
    location.pathname === "/checkout-verify" ||
    location.pathname === "/login" ||
    location.pathname === "/orders"
  ) {
    return null;
  }

  return (
    <button
      type="button"
      className="floating-cart"
      onClick={() => (onOpenCart ? onOpenCart() : navigate("/cart"))}
      aria-label={`Open cart${cartCount > 0 ? `, ${cartCount} items` : ""}`}
    >
      <IoBagOutline aria-hidden size={26} />
      <span className="floating-cart-count">
        {cartCount > 9 ? "9+" : cartCount}
      </span>
    </button>
  );
}
function Navigation({ cartCount, onOpenCart }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [customerSession, setCustomerSession] = useState(() => getCustomerSession());
  const profileWrapRef = useRef(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const isActive = (path) => location.pathname === path;

  const isHome = location.pathname === "/";

  useEffect(() => {
    setCustomerSession(getCustomerSession());
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isHome) {
      setHeaderScrolled(false);
      return undefined;
    }

    const threshold = 64;
    const onScroll = () => {
      setHeaderScrolled(window.scrollY > threshold);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  useEffect(() => {
    if (!profileOpen) return;
    const onDoc = (e) => {
      if (profileWrapRef.current && !profileWrapRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [profileOpen]);

  const collections = [
    {
      name: "Christmas Hampers",
      category: "Festive",
      filter: { search: "Christmas" },
    },
    { name: "Diwali Gift Boxes", category: "Festive", filter: { search: "Diwali" } },
    {
      name: "Birthday Surprises",
      category: "Occasions",
      filter: { search: "Birthday" },
    },
    { name: "Wedding Gifts", category: "Occasions", filter: { search: "Wedding" } },
    {
      name: "Anniversary Keepsakes",
      category: "Occasions",
      filter: { search: "Anniversary" },
    },
    {
      name: "Corporate Gifting",
      category: "Business",
      filter: { search: "Corporate" },
    },
    {
      name: "Thank You Boxes",
      category: "Business",
      filter: { search: "Thank You" },
    },
    {
      name: "Self-Care Kits",
      category: "Lifestyle",
      filter: { search: "Self-Care" },
    },
    {
      name: "Gourmet Treats",
      category: "Lifestyle",
      filter: { search: "Gourmet" },
    },
    { name: "Custom Notes", category: "Personalised", filter: { search: "Custom" } },
  ];

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const logout = () => {
    clearCustomerSession();
    setCustomerSession(null);
    setProfileOpen(false);
    closeMenu();
    navigate("/");
  };

  const handleCollectionClick = (filter) => {
    navigate("/shop", { state: { filter } });
    closeMenu();
  };

  const groupedCollections = collections.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <>
      <header
        className={`site-header${
          isHome
            ? headerScrolled
              ? " site-header--glass"
              : " site-header--transparent"
            : ""
        }`}
      >
        <nav>
          <Link to="/" className="logo logo--brand" onClick={closeMenu}>
            <img
              src={APP_LOGO}
              alt={APP_LOGO_ALT}
              className="logo__mark logo__mark--brand"
              width={52}
              height={52}
            />
          </Link>
          <div className="nav-mid">
            <ul className="nav-links">
              <li>
                <Link to="/" className={isActive("/") ? "active" : ""}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/shop" className={isActive("/shop") ? "active" : ""}>
                  Shop
                </Link>
              </li>
              <li>
                <Link to="/catalogue" className={isActive("/catalogue") ? "active" : ""}>
                  Assembly Instructions
                </Link>
              </li>
             {/*  <li>
                <Link
                  to="/gift-guide"
                  className={isActive("/gift-guide") ? "active" : ""}
                >
                  Collections
                </Link>
              </li>
              <li>
                <Link
                  to="/customise"
                  className={location.pathname.startsWith("/customise") ? "active" : ""}
                >
                  Customise
                </Link>
              </li> */}
              <li>
                <Link to="/about" className={isActive("/about") ? "active" : ""}>
                  About
                </Link>
              </li>
              {customerSession ? (
                <li className="nav-profile" ref={profileWrapRef}>
                  <button
                    type="button"
                    className={`nav-profile-trigger${isActive("/orders") ? " active" : ""}`}
                    aria-expanded={profileOpen}
                    aria-haspopup="true"
                    onClick={() => setProfileOpen((o) => !o)}
                  >
                    Profile
                    <span className="nav-profile-chevron" aria-hidden>
                      ▾
                    </span>
                  </button>
                  {profileOpen && (
                    <div className="nav-profile-dropdown" role="menu">
                      <Link
                        to="/orders"
                        role="menuitem"
                        className={isActive("/orders") ? "active" : ""}
                        onClick={() => setProfileOpen(false)}
                      >
                        Orders
                      </Link>
                      <button type="button" role="menuitem" onClick={logout}>
                        <BtnContent icon={IoLogOutOutline} iconSize={14}>
                          Log out
                        </BtnContent>
                      </button>
                    </div>
                  )}
                </li>
              ) : (
                <li>
                  <Link to="/login" className={isActive("/login") ? "active" : ""}>
                    Log in
                  </Link>
                </li>
              )}
            </ul>
            <div className="nav-tools">
              <button
                type="button"
                className="nav-icon-btn"
                aria-label="Search shop"
                onClick={() => navigate("/shop")}
              >
                <IoSearchOutline aria-hidden size={22} />
              </button>
              <button
                type="button"
                className="nav-icon-btn nav-cart-trigger"
                aria-label={`Open cart${cartCount > 0 ? `, ${cartCount} items` : ""}`}
                onClick={onOpenCart}
              >
                <IoBagOutline aria-hidden size={22} />
                {cartCount > 0 && (
                  <motion.span
                    className="nav-badge"
                    key={cartCount}
                    initial={{ scale: 0.65 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 520, damping: 24 }}
                  >
                    {cartCount > 9 ? "9+" : cartCount}
                  </motion.span>
                )}
              </button>
            </div>
          </div>
          <button
            type="button"
            className={`hamburger ${menuOpen ? "active" : ""}`}
            onClick={toggleMenu}
            aria-expanded={menuOpen}
            aria-controls="site-mobile-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            <span />
            <span />
            <span />
          </button>
        </nav>
      </header>

      <div
        className={`menu-overlay ${menuOpen ? "active" : ""}`}
        onClick={closeMenu}
      ></div>

      <div
        id="site-mobile-nav"
        className={`mobile-menu mobile-menu--lux ${menuOpen ? "active" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation and gift categories"
      >
        <div className="mobile-menu-header">
          <h3>Gift categories</h3>
          <button className="close-menu" onClick={closeMenu}>
            ✕
          </button>
        </div>

        <ul className="collections-menu">
          {Object.entries(groupedCollections).map(([category, items]) => (
            <React.Fragment key={category}>
              <li className="collection-category">{category}</li>
              {items.map((collection) => (
                <li key={collection.name}>
                  <button
                    type="button"
                    className="collection-menu-button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCollectionClick(collection.filter);
                    }}
                  >
                    {collection.name}
                  </button>
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>

        <ul className="mobile-nav-links">
          <li>
            <Link
              to="/"
              className={isActive("/") ? "active" : ""}
              onClick={closeMenu}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/shop"
              className={isActive("/shop") ? "active" : ""}
              onClick={closeMenu}
            >
              Shop
            </Link>
          </li>
          <li>
            <Link
              to="/catalogue"
              className={isActive("/catalogue") ? "active" : ""}
              onClick={closeMenu}
            >
              Assembly Instructions
            </Link>
          </li>
          <li>
            <Link
              to="/gift-guide"
              className={isActive("/gift-guide") ? "active" : ""}
              onClick={closeMenu}
            >
              Collections
            </Link>
          </li>
          <li>
            <Link
              to="/customise"
              className={location.pathname.startsWith("/customise") ? "active" : ""}
              onClick={closeMenu}
            >
              Customise
            </Link>
          </li>
          <li>
            <Link
              to="/about"
              className={isActive("/about") ? "active" : ""}
              onClick={closeMenu}
            >
              About
            </Link>
          </li>
          {customerSession ? (
            <>
              <li className="mobile-nav-profile-label">
                <span className="text-muted small">Signed in</span>
                <span className="small d-block">{customerSession.email}</span>
              </li>
              <li>
                <Link
                  to="/orders"
                  className={isActive("/orders") ? "active" : ""}
                  onClick={closeMenu}
                >
                  Orders
                </Link>
              </li>
              <li>
                <button type="button" className="mobile-nav-logout" onClick={logout}>
                  <BtnContent icon={IoLogOutOutline} iconSize={14}>
                    Log out
                  </BtnContent>
                </button>
              </li>
            </>
          ) : (
            <li>
              <Link
                to="/login"
                className={isActive("/login") ? "active" : ""}
                onClick={closeMenu}
              >
                Log in
              </Link>
            </li>
          )}
          <li>
            <button
              type="button"
              className="mobile-nav-cart-link"
              onClick={() => {
                onOpenCart();
                closeMenu();
              }}
            >
              <BtnContent icon={IoCartOutline} iconSize={14}>
                Cart{cartCount > 0 ? ` (${cartCount})` : ""}
              </BtnContent>
            </button>
          </li>
        </ul>
      </div>
    </>
  );
}

function Footer() {
  return (
    <footer className="site-footer site-footer--lux">
      <div className="site-footer__grid">
        <div className="site-footer__brand">
          <p className="site-footer__title">Rencin Gifting</p>
          <p className="site-footer__lead">
            Curated gift boxes, festive hampers, and thoughtful delivery across India.
          </p>
        </div>
        <nav className="site-footer__col" aria-label="Shop">
          <h2 className="site-footer__heading">Shop</h2>
          <ul className="site-footer__links">
            <li>
              <Link to="/shop">All products</Link>
            </li>
            <li>
              <Link to="/catalogue">Assembly instructions</Link>
            </li>
            <li>
              <Link to="/gift-guide">Collections</Link>
            </li>
          </ul>
        </nav>
        <nav className="site-footer__col" aria-label="Customer service">
          <h2 className="site-footer__heading">Customer service</h2>
          <ul className="site-footer__links">
            <li>
              <a href={`mailto:${CONTACT_EMAIL}`}>Email us</a>
            </li>
            <li>
              <Link to="/about">About us</Link>
            </li>
            <li>
              <Link to="/privacy">Privacy policy</Link>
            </li>
            <li>
              <Link to="/shipping">Shipping policy</Link>
            </li>
            <li>
              <Link to="/returns">Return policy</Link>
            </li>
            <li>
              <Link to="/">Home</Link>
            </li>
          </ul>
        </nav>
        <div className="site-footer__col site-footer__col--contact">
          <h2 className="site-footer__heading">Contact</h2>
          <p className="site-footer__contact-name">{BUSINESS_NAME}</p>
          <p className="site-footer__contact-care">C/O: {BUSINESS_CARE_OF}</p>
          <p className="site-footer__contact-line">
            <span className="site-footer__icon" aria-hidden>
              <LocationIcon size={18} color="var(--footer-muted)" />
            </span>
            <span className="site-footer__address">
              {BUSINESS_ADDRESS_LINES.map((line) => (
                <span key={line} className="site-footer__address-line">
                  {line}
                </span>
              ))}
            </span>
          </p>
          <p className="site-footer__contact-line">
            <span className="site-footer__icon" aria-hidden>
              <EmailIcon size={18} color="var(--footer-muted)" />
            </span>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
          <p className="site-footer__contact-line">
            <span className="site-footer__icon" aria-hidden>
              <IoCallOutline size={18} color="var(--footer-muted)" />
            </span>
            <a href={`tel:+${CONTACT_PHONE_E164}`}>{CONTACT_PHONE_DISPLAY}</a>
          </p>
        </div>
      </div>
      <div className="site-footer__social" aria-label="Social media">
        <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
          Instagram
        </a>
      </div>
      <div className="site-footer__payments" aria-label="Accepted payments">
        <span className="site-footer__pay">UPI</span>
        <span className="site-footer__pay">Visa</span>
        <span className="site-footer__pay">Mastercard</span>
        <span className="site-footer__pay">RuPay</span>
        <span className="site-footer__pay">Netbanking</span>
      </div>
      <div className="site-footer__bottom">
        <p>&copy; {new Date().getFullYear()} Rencin Gifting. All rights reserved.</p>
        <p className="site-footer__udyam">
          UDYAM Registration No.: {UDYAM_REGISTRATION_NUMBER}
        </p>
      </div>
    </footer>
  );
}

export default App;
